import React, { useState } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Button, 
  Typography, 
  Space, 
  message, 
  Divider,
  Alert
} from 'antd';
import { 
  PhoneOutlined, 
  MailOutlined, 
  KeyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Text, Title } = Typography;

const OTPVerification = ({ 
  visible, 
  onClose, 
  type = 'mobile', // 'mobile' or 'email'
  currentValue = '', // current mobile/email value
  onVerificationSuccess 
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [verifying, setVerifying] = useState(false);

  const handleSendOTP = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const endpoint = type === 'mobile' ? '/api/users/send-mobile-otp' : '/api/users/send-email-otp';
      const data = type === 'mobile' ? { mobile: currentValue } : { email: currentValue };
      
      const response = await axios.post(endpoint, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        message.success('OTP sent successfully!');
        setOtpSent(true);
        startCountdown();
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      message.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (values) => {
    try {
      setVerifying(true);
      const token = localStorage.getItem('token');
      
      const endpoint = type === 'mobile' ? '/api/users/verify-mobile-otp' : '/api/users/verify-email-otp';
      const data = type === 'mobile' 
        ? { mobile: currentValue, otp: values.otp }
        : { email: currentValue, otp: values.otp };
      
      const response = await axios.post(endpoint, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        message.success(`${type === 'mobile' ? 'Mobile' : 'Email'} verified successfully!`);
        onVerificationSuccess && onVerificationSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      message.error(error.response?.data?.message || 'Failed to verify OTP');
    } finally {
      setVerifying(false);
    }
  };

  const startCountdown = () => {
    setCountdown(60); // 60 seconds
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOTP = () => {
    if (countdown === 0) {
      handleSendOTP();
    }
  };

  const getTitle = () => {
    return type === 'mobile' ? 'Verify Mobile Number' : 'Verify Email Address';
  };

  const getIcon = () => {
    return type === 'mobile' ? <PhoneOutlined /> : <MailOutlined />;
  };

  const getFieldLabel = () => {
    return type === 'mobile' ? 'Mobile Number' : 'Email Address';
  };

  const getFieldValue = () => {
    return currentValue;
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {getIcon()}
          {getTitle()}
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={500}
      destroyOnClose
    >
      <div style={{ padding: '20px 0' }}>
        <Alert
          message={`Verify your ${type === 'mobile' ? 'mobile number' : 'email address'}`}
          description={`We'll send a 6-digit OTP to ${getFieldValue()}`}
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <div style={{ marginBottom: '24px' }}>
          <Text strong>{getFieldLabel()}: </Text>
          <Text>{getFieldValue()}</Text>
        </div>

        {!otpSent ? (
          <Button 
            type="primary" 
            onClick={handleSendOTP} 
            loading={loading}
            block
            size="large"
          >
            Send OTP
          </Button>
        ) : (
          <Form
            form={form}
            onFinish={handleVerifyOTP}
            layout="vertical"
          >
            <Form.Item
              name="otp"
              label="Enter 6-digit OTP"
              rules={[
                { required: true, message: 'Please enter OTP' },
                { len: 6, message: 'OTP must be 6 digits' },
                { pattern: /^[0-9]{6}$/, message: 'OTP must contain only numbers' }
              ]}
            >
              <Input
                prefix={<KeyOutlined />}
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                size="large"
                style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '4px' }}
              />
            </Form.Item>

            <Form.Item>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={verifying}
                  block
                  size="large"
                >
                  Verify OTP
                </Button>
                
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary">
                    Didn't receive OTP?{' '}
                    {countdown > 0 ? (
                      <Text type="secondary">
                        Resend in <ClockCircleOutlined /> {countdown}s
                      </Text>
                    ) : (
                      <Button 
                        type="link" 
                        onClick={handleResendOTP}
                        style={{ padding: 0 }}
                      >
                        Resend OTP
                      </Button>
                    )}
                  </Text>
                </div>
              </Space>
            </Form.Item>
          </Form>
        )}

        <Divider />

        <div style={{ textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            OTP expires in 10 minutes
          </Text>
        </div>
      </div>
    </Modal>
  );
};

export default OTPVerification;
